import React, { useCallback, useEffect, useState } from "react";
import moment from "moment";
import {
    FeedComposer,
    FeedComposerButton,
    FeedComposerInput,
    FeedComment,
    FeedCommentBubble,
    FeedCommentMeta,
    FeedCommentName,
    FeedCommentPreviewList,
    FeedCommentText,
    FeedInlineState,
    FeedReplyButton,
    FeedReplyList,
    FeedTextButton
} from "src/components/NoticeboardFeedUi";
import { createNoticeComment, getCommentReplies, getNoticeComments } from "../../api/feedApi";
import {
    getDisplayName,
    getReplyPlaceholder,
    sanitizeNoticeboardComment
} from "../../feedUtils";
import NoticeboardFeedAvatar from "./NoticeboardFeedAvatar";

export const NoticeboardComment = ({
    comment,
    token,
    interactionUuid,
    showReplies,
    onReply,
    compact
}) => {
    const displayName = getDisplayName(comment.firstName, comment.lastName, "Unavailable user");
    const [replies, setReplies] = useState([]);
    const [repliesPage, setRepliesPage] = useState(1);
    const [nextRepliesPageUrl, setNextRepliesPageUrl] = useState(null);
    const [repliesLoading, setRepliesLoading] = useState(false);
    const [repliesError, setRepliesError] = useState(false);
    const [repliesOpened, setRepliesOpened] = useState(false);

    const loadReplies = useCallback(async (page) => {
        setRepliesLoading(true);
        setRepliesError(false);
        try {
            const response = await getCommentReplies(token, comment.uuid, page);
            setReplies(current => page === 1 ? response.data : [...current, ...response.data]);
            setNextRepliesPageUrl(response.nextPageUrl);
            setRepliesPage(page);
            setRepliesOpened(true);
        } catch {
            setRepliesError(true);
        } finally {
            setRepliesLoading(false);
        }
    }, [comment.uuid, token]);

    return (
        <FeedComment>
            <NoticeboardFeedAvatar
                displayName={displayName}
                profilePictureUrl={comment.profilePicture}
                size={compact ? "2rem" : "2.25rem"}
                fontSize="0.66rem"
            />
            <div>
                <FeedCommentBubble>
                    <FeedCommentName>{displayName}</FeedCommentName>
                    <FeedCommentText>{comment.comment}</FeedCommentText>
                    <FeedCommentMeta>
                        {moment(comment.createdAt).fromNow()}
                        {comment.editedAt ? " · Edited" : ""}
                        {comment.repliesCount ? ` · ${comment.repliesCount} ${comment.repliesCount === 1 ? "reply" : "replies"}` : ""}
                    </FeedCommentMeta>
                </FeedCommentBubble>
                {showReplies && (
                    <>
                        <FeedReplyButton type="button" onClick={() => onReply(comment)}>
                            Reply
                        </FeedReplyButton>
                        {comment.repliesCount > 0 && !repliesOpened && (
                            <FeedReplyButton
                                type="button"
                                disabled={repliesLoading}
                                onClick={() => loadReplies(1)}
                            >
                                {repliesLoading ? "Loading replies…" : `View ${comment.repliesCount} ${comment.repliesCount === 1 ? "reply" : "replies"}`}
                            </FeedReplyButton>
                        )}
                        {repliesError && (
                            <FeedReplyButton type="button" onClick={() => loadReplies(repliesPage)}>
                                Retry replies
                            </FeedReplyButton>
                        )}
                        {replies.length > 0 && (
                            <FeedReplyList aria-label={`Replies to ${displayName}`}>
                                {replies.map(reply => (
                                    <NoticeboardComment
                                        key={reply.uuid}
                                        comment={reply}
                                        token={token}
                                        interactionUuid={interactionUuid}
                                        showReplies={false}
                                        onReply={onReply}
                                        compact
                                    />
                                ))}
                                {nextRepliesPageUrl && (
                                    <FeedTextButton
                                        type="button"
                                        disabled={repliesLoading}
                                        onClick={() => loadReplies(repliesPage + 1)}
                                    >
                                        {repliesLoading ? "Loading…" : "Load more replies"}
                                    </FeedTextButton>
                                )}
                            </FeedReplyList>
                        )}
                    </>
                )}
            </div>
        </FeedComment>
    );
};

const mergeComments = (existingComments, additionalComments) => {
    const commentsByUuid = new Map(existingComments.map(comment => [comment.uuid, comment]));
    additionalComments.forEach(comment => commentsByUuid.set(comment.uuid, comment));
    return Array.from(commentsByUuid.values());
};

const NoticeboardFeedCommentPreview = ({
    token,
    noticeUuid,
    interactionUuid,
    commentCount,
    allowComments,
    showComposer,
    onCommentCreated
}) => {
    const [comments, setComments] = useState([]);
    const [page, setPage] = useState(1);
    const [nextPageUrl, setNextPageUrl] = useState(null);
    const [initialLoading, setInitialLoading] = useState(false);
    const [moreLoading, setMoreLoading] = useState(false);
    const [initialError, setInitialError] = useState(false);
    const [moreError, setMoreError] = useState(false);
    const [commentText, setCommentText] = useState("");
    const [replyingTo, setReplyingTo] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(false);

    const loadPage = useCallback(async (requestedPage, replaceComments) => {
        if (requestedPage === 1) {
            setInitialLoading(true);
            setInitialError(false);
        } else {
            setMoreLoading(true);
            setMoreError(false);
        }
        try {
            const response = await getNoticeComments(token, interactionUuid, requestedPage);
            setComments(current => (
                replaceComments ? response.data : mergeComments(current, response.data)
            ));
            setNextPageUrl(response.nextPageUrl);
            setPage(requestedPage);
        } catch {
            if (requestedPage === 1) {
                setInitialError(true);
            } else {
                setMoreError(true);
            }
        } finally {
            if (requestedPage === 1) {
                setInitialLoading(false);
            } else {
                setMoreLoading(false);
            }
        }
    }, [interactionUuid, token]);

    useEffect(() => {
        setComments([]);
        setPage(1);
        setNextPageUrl(null);
        setInitialError(false);
        setMoreError(false);
        setCommentText("");
        setReplyingTo(null);
        if (commentCount > 0) {
            loadPage(1, true);
        }
    }, [interactionUuid, loadPage]);

    const handleSubmit = async (event) => {
        event.preventDefault();
        const sanitizedComment = sanitizeNoticeboardComment(commentText).trim();
        if (!sanitizedComment || submitting) {
            return;
        }

        const target = interactionUuid === noticeUuid ?
            { noticeUuid } :
            { publicationUuid: interactionUuid };
        setSubmitting(true);
        setSubmitError(false);
        try {
            const createdComment = await createNoticeComment(
                token,
                target,
                sanitizedComment,
                replyingTo ? replyingTo.uuid : null
            );
            if (!replyingTo) {
                setComments(current => mergeComments(current, [createdComment]));
            } else {
                await loadPage(1, true);
            }
            setCommentText("");
            setReplyingTo(null);
            onCommentCreated();
        } catch {
            setSubmitError(true);
        } finally {
            setSubmitting(false);
        }
    };

    const composerIsVisible = showComposer || Boolean(replyingTo);
    if (!commentCount && !composerIsVisible) {
        return null;
    }

    return (
        <FeedCommentPreviewList aria-label="Post comments">
            {initialLoading && <FeedInlineState $compact>Loading comments…</FeedInlineState>}
            {initialError && (
                <FeedInlineState $compact $error>
                    Comments could not be loaded.{" "}
                    <FeedTextButton type="button" onClick={() => loadPage(1, true)}>Retry</FeedTextButton>
                </FeedInlineState>
            )}
            {!initialLoading && !initialError && comments.map(comment => (
                <NoticeboardComment
                    key={comment.uuid}
                    comment={comment}
                    token={token}
                    interactionUuid={interactionUuid}
                    showReplies
                    onReply={setReplyingTo}
                    compact
                />
            ))}
            {nextPageUrl && (
                <FeedTextButton
                    type="button"
                    disabled={moreLoading}
                    onClick={() => loadPage(page + 1, false)}
                >
                    {moreLoading ? "Loading more comments…" : "View more comments"}
                </FeedTextButton>
            )}
            {moreError && (
                <FeedInlineState $compact $error>
                    More comments could not be loaded.{" "}
                    <FeedTextButton type="button" onClick={() => loadPage(page + 1, false)}>
                        Retry
                    </FeedTextButton>
                </FeedInlineState>
            )}
            {composerIsVisible && allowComments && (
                <FeedComposer onSubmit={handleSubmit}>
                    <FeedComposerInput
                        value={commentText}
                        onChange={event => setCommentText(event.target.value)}
                        placeholder={replyingTo ? getReplyPlaceholder(replyingTo) : "Write a comment…"}
                        aria-label={replyingTo ? getReplyPlaceholder(replyingTo) : "Write a comment"}
                    />
                    <FeedComposerButton type="submit" disabled={!commentText.trim() || submitting}>
                        {submitting ? "Posting…" : replyingTo ? "Reply" : "Post"}
                    </FeedComposerButton>
                    {replyingTo && (
                        <FeedTextButton type="button" onClick={() => setReplyingTo(null)}>
                            Cancel reply
                        </FeedTextButton>
                    )}
                </FeedComposer>
            )}
            {composerIsVisible && !allowComments && (
                <FeedInlineState $compact>Comments are turned off for this post.</FeedInlineState>
            )}
            {submitError && (
                <FeedInlineState $compact $error role="alert">
                    Your comment could not be posted. Please try again.
                </FeedInlineState>
            )}
        </FeedCommentPreviewList>
    );
};

export default NoticeboardFeedCommentPreview;
